import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import api from "@/services/api.js";
import logo from "@/assets/logo.png";
import toast from "react-hot-toast";
import { capture } from "@/instrument";
import { useNavigate } from "react-router-dom";
import { HiOutlineTrash, HiOutlinePlus, HiChevronDown, HiOutlineXCircle } from "react-icons/hi";
import { useLoading } from "@/context/LoadingContext.jsx";
import AbandonModal from "@/components/AbandonModal";
import DateInput from "@/components/DateInput";
import TimeInput from "@/components/TimeInput";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { compareByOrder } from "../utils";

const getCurrentSegment = (answer) => {
  if (!answer) return "";
  const parts = answer.split(";");
  return parts[parts.length - 1].trim();
};

const Questions = () => {
  const navigate = useNavigate();
  const isMobileDevice = useMemo(() => /Mobi|Android/i.test(navigator.userAgent), []);
  const timeoutRef = useRef(null);
  const { startLoading, stopLoading, isLoading } = useLoading();

  const questionRecordDocumentPickers = useRef({});
  const [questionRecords, setQuestionRecords] = useState([]);
  const [admission, setAdmission] = useState(null);
  const [questionRecordDocuments, setQuestionRecordDocuments] = useState({});
  const [validate, setValidate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  const [dragOver, setDragOver] = useState({});
  const [showAbandonModal, setShowAbandonModal] = useState(false);
  const [openAutocompleteQuestionRecordId, setOpenAutocompleteQuestionRecordId] = useState(null);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [animateParentRef] = useAutoAnimate();

  const handleFile = async (file, questionRecordId) => {
    if (!file || !questionRecordId) return;

    const acceptedTypes = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
    if (!acceptedTypes.includes(file.type)) {
      toast.error("Format de fichier non supporté. Veuillez utiliser une image (PNG, JPG) ou un PDF.");
      return;
    }

    const fileType = file.type === "application/pdf" ? "pdf" : "image";
    const fileUrl = URL.createObjectURL(file);

    setQuestionRecordDocuments((prev) => ({
      ...prev,
      [questionRecordId]: { preview: { type: fileType, url: fileUrl, name: file.name }, file },
    }));

    const questionRecord = questionRecords.find((qr) => qr._id === questionRecordId);
    if (!questionRecord) return toast.error("Erreur lors de la mise à jour des réponses. Veuillez réessayer.");
    await updateQuestionRecords([questionRecord], [{ file, name: `document-${questionRecord.questionId}` }]);
  };

  const handleDragOver = (e, questionRecordId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver((prev) => ({ ...prev, [questionRecordId]: true }));
  };

  const handleDragLeave = (e, questionRecordId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver((prev) => ({ ...prev, [questionRecordId]: false }));
  };

  const handleDrop = (e, questionRecordId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver((prev) => ({ ...prev, [questionRecordId]: false }));

    const file = e.dataTransfer.files[0];
    if (file) handleFile(file, questionRecordId);
  };

  useEffect(() => {
    getInitialData();
  }, []);

  const getInitialData = async () => {
    try {
      startLoading();

      const res = await api.get(`/admission`);
      if (!res.ok) throw res;

      if (res.data?.status !== "DRAFT") {
        navigate("/");
        return stopLoading();
      }

      let fetchedQuestionRecords = res.data.questionRecords;
      if (res.data.type === "LATER") fetchedQuestionRecords = fetchedQuestionRecords.filter((qr) => qr.canAnswerInPreRegistration);

      setAdmission(res.data);
      setQuestionRecords(fetchedQuestionRecords);

      let existingDocuments = {};
      for (const questionRecord of fetchedQuestionRecords) {
        if (questionRecord.answerType === "DOCUMENT" && questionRecord.answer) {
          existingDocuments[questionRecord._id] = {
            preview: { name: questionRecord.answer.split("/").pop() || "Document" },
            file: null,
          };
        }
      }
      setQuestionRecordDocuments(existingDocuments);
    } catch (e) {
      if (e?.code === "UNAUTHORIZED") return navigate("/");
      capture(e);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      stopLoading();
    }
  };

  const updateQuestionRecords = async (questionRecordsToUpdate, filesObj = []) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("questionRecords", JSON.stringify(questionRecordsToUpdate));
      formData.append("isFinalSubmission", "false");
      formData.append("checkIn", String(admission?.type === "NOW"));

      for (const { name, file } of filesObj) {
        formData.append(name, file);
      }

      const res = await api.putFormData(`/admission/questionRecord`, formData);

      if (!res.ok) throw res;

      let updatedQuestionRecords = res.data.questionRecords;
      if (res.data.type === "LATER") updatedQuestionRecords = updatedQuestionRecords.filter((qr) => qr.canAnswerInPreRegistration);

      setQuestionRecords((prev) =>
        updatedQuestionRecords.map((questionRecord) => {
          const existingQuestionRecord = prev.find((qr) => qr._id === questionRecord._id);
          if (existingQuestionRecord && questionRecord.answerType !== "DOCUMENT") return existingQuestionRecord;
          return questionRecord;
        }),
      );
    } catch (e) {
      if (e?.code === "UNAUTHORIZED") return navigate("/");
      capture(e);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    }
    setLoading(false);
  };

  const handleAnswerChange = useCallback(
    async (questionRecordId, value) => {
      const updatedQuestionRecords = JSON.parse(JSON.stringify(questionRecords));
      const questionRecord = updatedQuestionRecords.find((qr) => qr._id === questionRecordId);
      if (!questionRecord) return;

      questionRecord.answer = value;
      setQuestionRecords(updatedQuestionRecords);

      setLoading(true);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => updateQuestionRecords(updatedQuestionRecords), 250);
    },
    [questionRecords, updateQuestionRecords],
  );

  const submitAdmission = async () => {
    setSubmissionAttempted(true);

    const unansweredQuestionRecords = questionRecords.filter((questionRecord) => {
      if (questionRecord.patientIsRequired === false) return false;
      if (questionRecord.answerType === "DOCUMENT") {
        return !questionRecordDocuments[questionRecord._id];
      }
      return !questionRecord.answer;
    });
    if (unansweredQuestionRecords.length > 0) {
      const questionRecordElement = document.querySelector(`[data-question-record-id="${unansweredQuestionRecords[0]._id}"]`);
      if (questionRecordElement) questionRecordElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => questionRecordElement?.focus(), 300);
      return toast.error(`${unansweredQuestionRecords.length} question${unansweredQuestionRecords.length > 1 ? "s" : ""} sans réponse`);
    }

    if (!validate) {
      const validateCheckbox = document.getElementById("validate");
      if (validateCheckbox) validateCheckbox?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => validateCheckbox?.focus(), 300);
      return toast.error("Veuillez certifier que les informations sont exactes.");
    }

    try {
      startLoading();

      const formData = new FormData();
      formData.append("questionRecords", JSON.stringify(questionRecords));
      formData.append("isFinalSubmission", "true");
      formData.append("checkIn", String(admission?.type === "NOW"));
      for (const questionRecord of questionRecords) {
        const file = questionRecordDocuments[questionRecord._id]?.file;
        if (file) formData.append(`document-${questionRecord.questionId}`, file);
      }

      const res = await api.putFormData(`/admission/questionRecord`, formData);
      if (!res.ok) throw res;

      if (admission?.type === "LATER") return navigate("/confirmation");
      return navigate("/guidelines");
    } catch (e) {
      if (e?.code === "UNAUTHORIZED") return navigate("/");
      capture(e);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    }
    stopLoading();
  };

  const isQuestionRecordUnanswered = (questionRecord) => {
    if (!submissionAttempted) return false;
    if (questionRecord.patientIsRequired === false) return false;
    if (questionRecord.answerType === "DOCUMENT") return !questionRecordDocuments[questionRecord._id]?.preview?.name;
    return !questionRecord.answer;
  };

  if (isLoading) return null;

  return (
    <div className="container mx-auto min-w-full font-sans text-center">
      <div className="p-5 pb-44">
        <div className="mt-5">
          <img src={logo} alt="Logo" className="mx-auto h-12" />
        </div>
        <div className="container mx-auto font-sans">
          <div className="mt-4 w-full text-left p-2 rounded-lg">
            {questionRecords?.length > 0 && (
              <>
                <p className="font-bold text-lg text-primary">Questions</p>
                <div ref={animateParentRef}>
                  {[...questionRecords]
                    .sort(compareByOrder)
                    .map((questionRecord) => {
                      const choicesWithLabels = (questionRecord.options || []).map((o) => ({ optionId: o.optionId, value: o.label }));
                      return (
                      <div key={questionRecord._id} className="mt-4 mb-4 text-left">
                        <p className="font-semibold">
                          {questionRecord.question || ""}
                          {questionRecord.patientIsRequired !== false && <span className="text-red-500 ml-1">*</span>}
                        </p>

                        {questionRecord.answerType === "MULTIPLE_CHOICE" ? (
                          <div className="relative">
                            <select
                              data-question-record-id={questionRecord._id}
                              value={questionRecord.answer || ""}
                              onChange={(e) => handleAnswerChange(questionRecord._id, e.target.value)}
                              className={`block w-full mt-2 py-2 px-3 bg-white border ${
                                isQuestionRecordUnanswered(questionRecord) ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
                              } rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary h-10 min-w-full box-border`}
                            >
                              <option value="" disabled>
                                Sélectionnez une réponse
                              </option>
                              {choicesWithLabels.map((choice) => (
                                <option key={choice.optionId} value={choice.optionId}>
                                  {choice.value}
                                </option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-primary">
                              <HiChevronDown className="w-5 h-5" />
                            </div>
                            {isQuestionRecordUnanswered(questionRecord) && <p className="text-red-500 text-xs mt-1">Cette question requiert une réponse</p>}
                          </div>
                        ) : questionRecord.answerType === "TEXT" ? (
                          <>
                            <div className="relative">
                              <input
                                type="text"
                                data-question-record-id={questionRecord._id}
                                value={questionRecord.answer || ""}
                                onChange={(e) => {
                                  handleAnswerChange(questionRecord._id, e.target.value);
                                  if (choicesWithLabels.length > 0) {
                                    setOpenAutocompleteQuestionRecordId(questionRecord._id);
                                    setSelectedSuggestionIndex(0);
                                  }
                                }}
                                onFocus={() => {
                                  if (choicesWithLabels.length > 0) {
                                    setOpenAutocompleteQuestionRecordId(questionRecord._id);
                                    setSelectedSuggestionIndex(0);
                                  }
                                }}
                                onBlur={() => {
                                  setTimeout(() => {
                                    setOpenAutocompleteQuestionRecordId(null);
                                    setSelectedSuggestionIndex(0);
                                  }, 200);
                                }}
                                onKeyDown={(e) => {
                                  if (choicesWithLabels.length > 0 && openAutocompleteQuestionRecordId === questionRecord._id) {
                                    const currentSegment = getCurrentSegment(questionRecord.answer);
                                    const filtered = choicesWithLabels.filter(
                                      (s) => !currentSegment || s.value.toLowerCase().includes(currentSegment.toLowerCase()),
                                    );

                                    if (e.key === "ArrowDown") {
                                      e.preventDefault();
                                      setSelectedSuggestionIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : prev));
                                    } else if (e.key === "ArrowUp") {
                                      e.preventDefault();
                                      setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : 0));
                                    } else if (e.key === "Enter" && filtered.length > 0) {
                                      e.preventDefault();
                                      const selectedOption = filtered[selectedSuggestionIndex];
                                      if (selectedOption) {
                                        const existingParts = (questionRecord.answer || "").split(";").slice(0, -1).map((p) => p.trim()).filter(Boolean);
                                        const newAnswer = [...existingParts, selectedOption.value].join("; ") + "; ";
                                        handleAnswerChange(questionRecord._id, newAnswer);
                                        setSelectedSuggestionIndex(0);
                                      }
                                    } else if (e.key === "Escape") {
                                      setOpenAutocompleteQuestionRecordId(null);
                                      setSelectedSuggestionIndex(0);
                                    }
                                  }
                                }}
                                className={`block w-full mt-2 py-2 px-3 bg-white border ${
                                  isQuestionRecordUnanswered(questionRecord) ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
                                } rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary h-10 min-w-full box-border`}
                                placeholder="Votre réponse"
                              />
                              {choicesWithLabels.length > 0 && openAutocompleteQuestionRecordId === questionRecord._id && (() => {
                                const currentSegment = getCurrentSegment(questionRecord.answer);
                                const filtered = choicesWithLabels.filter(
                                  (s) => !currentSegment || s.value.toLowerCase().includes(currentSegment.toLowerCase()),
                                );
                                if (filtered.length === 0) return null;
                                return (
                                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                                    {filtered.map((option, sugIndex) => (
                                      <button
                                        key={sugIndex}
                                        type="button"
                                        className={`w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                                          sugIndex === selectedSuggestionIndex ? "bg-gray-100" : ""
                                        }`}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          const existingParts = (questionRecord.answer || "").split(";").slice(0, -1).map((p) => p.trim()).filter(Boolean);
                                          const newAnswer = [...existingParts, option.value].join("; ") + "; ";
                                          handleAnswerChange(questionRecord._id, newAnswer);
                                          setSelectedSuggestionIndex(0);
                                          setOpenAutocompleteQuestionRecordId(null);
                                        }}
                                        onMouseEnter={() => setSelectedSuggestionIndex(sugIndex)}
                                      >
                                        {option.value}
                                      </button>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                            {isQuestionRecordUnanswered(questionRecord) && <p className="text-red-500 text-xs mt-1">Cette question requiert une réponse</p>}
                          </>
                        ) : questionRecord.answerType === "DATE" ? (
                          <>
                            <DateInput
                              data-question-record-id={questionRecord._id}
                              value={questionRecord.answer || ""}
                              onChange={(date) => handleAnswerChange(questionRecord._id, date)}
                              className={`block w-full mt-2 py-2 px-3 bg-white border ${
                                isQuestionRecordUnanswered(questionRecord) ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
                              } rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary h-10 min-w-full box-border`}
                            />
                            {isQuestionRecordUnanswered(questionRecord) && <p className="text-red-500 text-xs mt-1">Cette question requiert une réponse</p>}
                          </>
                        ) : questionRecord.answerType === "HOUR" ? (
                          <>
                            <TimeInput
                              data-question-record-id={questionRecord._id}
                              value={questionRecord.answer || ""}
                              onChange={(time) => handleAnswerChange(questionRecord._id, time)}
                              className={`block w-full mt-2 py-2 px-3 bg-white border ${
                                isQuestionRecordUnanswered(questionRecord) ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
                              } rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary h-10 min-w-full box-border`}
                            />
                            {isQuestionRecordUnanswered(questionRecord) && <p className="text-red-500 text-xs mt-1">Cette question requiert une réponse</p>}
                          </>
                        ) : questionRecord.answerType === "DOCUMENT" ? (
                          <>
                            {questionRecordDocuments[questionRecord._id] ? (
                              <div className="relative group bg-gray-50 rounded-lg overflow-hidden shadow-sm max-h-[200px] mt-2">
                                <div className="aspect-w-4 aspect-h-3 h-[150px]">
                                  {questionRecordDocuments[questionRecord._id]?.preview?.type === "pdf" ? (
                                    <embed src={questionRecordDocuments[questionRecord._id].preview.url} type="application/pdf" className="w-full h-full pointer-events-none" />
                                  ) : questionRecordDocuments[questionRecord._id]?.preview?.url ? (
                                    <img
                                      src={questionRecordDocuments[questionRecord._id].preview.url}
                                      alt={`Document ${questionRecord.question || ""}`}
                                      className="object-contain w-full h-full"
                                    />
                                  ) : (
                                    <div className="flex items-center justify-center h-full text-gray-500">
                                      <p className="text-sm">Document téléchargé</p>
                                    </div>
                                  )}
                                </div>
                                <div className="p-3 flex items-center justify-between bg-white">
                                  <span className="text-sm font-medium text-gray-700 truncate">{questionRecordDocuments[questionRecord._id]?.preview?.name || "Document"}</span>
                                  <button
                                    onClick={() => {
                                      setQuestionRecordDocuments((prev) => {
                                        const newState = { ...prev };
                                        delete newState[questionRecord._id];
                                        return newState;
                                      });

                                      updateQuestionRecords([{ ...questionRecord, answer: "" }]);
                                    }}
                                    className="text-red-500 hover:text-red-700 transition-colors"
                                  >
                                    <HiOutlineTrash className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <input
                                  data-question-record-id={questionRecord._id}
                                  ref={(el) => {
                                    if (el) questionRecordDocumentPickers.current[questionRecord._id] = el;
                                  }}
                                  accept="image/png,image/jpeg,image/jpg,application/pdf"
                                  type="file"
                                  hidden
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) handleFile(file, questionRecord._id);
                                  }}
                                />
                                <div
                                  className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 transition-colors cursor-pointer mt-2 ${
                                    dragOver[questionRecord._id] ? "border-primary bg-primary/5" : "border-gray-300 hover:border-primary"
                                  } ${isQuestionRecordUnanswered(questionRecord) ? "border-red-500 ring-1 ring-red-500" : ""} text-gray-400 hover:text-primary`}
                                  onClick={() => questionRecordDocumentPickers.current[questionRecord._id]?.click()}
                                  onDragOver={(e) => handleDragOver(e, questionRecord._id)}
                                  onDragLeave={(e) => handleDragLeave(e, questionRecord._id)}
                                  onDrop={(e) => handleDrop(e, questionRecord._id)}
                                >
                                  <HiOutlinePlus className="w-8 h-8 mb-4" />
                                  <p className="text-lg font-medium text-gray-700 text-center">
                                    {isMobileDevice ? "Prendre une photo ou ajouter un document" : "Cliquez pour ajouter un document"}
                                  </p>
                                  <p className="text-sm text-gray-500 mt-1">ou glissez-déposez votre fichier ici</p>
                                </div>
                              </>
                            )}
                            {isQuestionRecordUnanswered(questionRecord) && <p className="text-red-500 text-xs mt-1">Cette question requiert une réponse</p>}
                          </>
                        ) : null}
                      </div>
                    );
                    })}
                </div>
              </>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-2 mx-auto w-fit">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="validate"
                className={`w-4 h-4 accent-primary border-gray-300 rounded focus:ring-primary flex-shrink-0 ${
                  submissionAttempted && !validate ? "ring-2 ring-red-500 ring-offset-1" : ""
                }`}
                onChange={() => setValidate((prev) => !prev)}
              />
              <label htmlFor="validate" className="ml-2 text-left">
                Je certifie que les informations fournies sont exactes.
                <span className="text-red-500 ml-1">*</span>
              </label>
            </div>
          </div>
        </div>

        {showAbandonModal && <AbandonModal onClose={() => setShowAbandonModal(false)} />}
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-t border-gray-200 shadow-lg px-5 py-4">
          <div className="container mx-auto max-w-2xl">
            <p className="text-sm text-gray-500 mb-2 text-center">
              <span className="text-red-500 mr-1">*</span>
              Veuillez remplir tous les champs obligatoires pour continuer
            </p>
            <div className="flex gap-4 items-center justify-center w-full">
              <button
                disabled={loading}
                className="px-4 py-2 rounded-md border-primary border text-primary hover:text-primary-600 hover:border-primary-600"
                onClick={() => navigate(-1)}
              >
                Retour
              </button>
              <button
                className="flex-1 px-4 py-2 rounded-md bg-primary text-white hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                onClick={submitAdmission}
                disabled={loading}
              >
                {loading ? "Chargement..." : "Valider ma demande"}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowAbandonModal(true)}
              className="w-full mt-1 px-4 py-2 rounded-md text-gray-500 hover:text-red-500 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <HiOutlineXCircle className="w-4 h-4" />
              Abandonner l'enregistrement
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Questions;
